<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  BarChart,
  Book,
  Bot,
  Brain,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  Globe,
  Key,
  Lightbulb,
  LineChart,
  Loader2,
  Lock,
  MessageCircle,
  Puzzle,
  Shield,
  Star,
  Target,
  User,
  Users,
} from 'lucide-vue-next'
import brochureCopyFr from './BrochureLandingCopy.json'
import brochureCopyEn from './BrochureLandingCopy_en.json'
import {
  formatPrice,
  pricingPlans,
} from '@/lib/stripe/plans'
import {
  subscribeToPlan,
} from '@/lib/stripe/stripe'
import { useToast } from '@/components/ui/toast/use-toast'
import trpc from '@/trpc'
import { Badge } from '@/components/ui/badge'

// Props to get session from parent (App.vue)
const props = defineProps({
  session: {
    type: Object,
    default: null,
  },
})

// Create a mapping for icons
const iconMap = {
  Puzzle,
  Calendar,
  Brain,
  Bot,
  Target,
  BarChart,
  Clock,
  LineChart,
  Shield,
  Lock,
  Eye,
  Key,
  User,
  Lightbulb,
  ChevronRight,
  ChevronLeft,
  MessageCircle,
  Book,
  CheckCircle,
  Star,
  Users,
  Globe,
}

const route = useRoute()
const router = useRouter()
const { toast } = useToast()

// Pricing state management
const isCreatingCheckout = ref(false)
const selectedPlanId = ref(null)

// Computed for user authentication
const isLoggedIn = computed(() => !!props.session)

// Determine current language and alternate language
const currentLang = computed(() => (route.name === 'landing_en' ? 'en' : 'fr'))
const alternateLang = computed(() =>
  currentLang.value === 'en' ? 'fr' : 'en',
)
const alternateRoute = computed(() =>
  currentLang.value === 'en' ? '/' : '/en',
)

// Determine which language to use based on route name
const brochureCopy = computed(() => {
  return currentLang.value === 'en' ? brochureCopyEn : brochureCopyFr
})

// Import all copy from JSON file based on language
const navigation = computed(() => brochureCopy.value.navigation)
const hero = computed(() => brochureCopy.value.hero)
const stats = computed(() =>
  brochureCopy.value.stats.map(stat => ({
    ...stat,
    icon: iconMap[stat.icon],
  })),
)
const trustedBy = computed(() => brochureCopy.value.trustedBy)
const testimonials = computed(() => brochureCopy.value.testimonials)
const featuresTitle = computed(() => brochureCopy.value.featuresTitle)
const testimonialHeader = computed(() => brochureCopy.value.testimonialHeader)

// Process features to map string icon names to actual components
const features = computed(() =>
  brochureCopy.value.features.map((feature) => {
    // Map main icon
    const processedFeature = {
      ...feature,
      icon: iconMap[feature.icon],
    }

    // Map resource list icons if present
    if (feature.resourceList) {
      processedFeature.resourceList = feature.resourceList.map(resource => ({
        ...resource,
        icon: iconMap[resource.icon],
      }))
    }

    return processedFeature
  }),
)

const securitySection = computed(() => ({
  ...brochureCopy.value.securitySection,
  features: brochureCopy.value.securitySection.features.map(feature => ({
    ...feature,
    icon: iconMap[feature.icon],
  })),
}))

// Use all 5 testimonials in the bottom block
const testimonialSlides = computed(() =>
  brochureCopy.value.testimonials.map(testimonial => ({
    quote: testimonial.messages.join(' '),
    name: testimonial.author.name,
    position: testimonial.author.position,
    company: testimonial.author.company,
    avatarUrl: testimonial.author.avatarUrl,
  })),
)
const bottomSection = computed(() => brochureCopy.value.bottomSection)

const ctaLink = '/beta'

// Pricing section translations
const pricingContent = computed(() => {
  return currentLang.value === 'en'
    ? {
        title: 'Choose your plan',
        description: 'Access all Selfwise features with the plan that suits you',
        popular: 'Most Popular',
        monthly: '/month',
        annual: '/year',
        lifetime: '',
        processing: 'Processing...',
        getStarted: 'Get Started',
      }
    : {
        title: 'Choisissez votre forfait',
        description: 'Accédez à toutes les fonctionnalités de Selfwise avec le forfait qui vous convient',
        popular: 'Le Plus Populaire',
        monthly: '/mois',
        annual: '/an',
        lifetime: '',
        processing: 'Traitement...',
        getStarted: 'Commencer',
      }
})

// Pricing functionality
async function handleSubscribeToPlan(plan) {
  // Skip for contact plans (enterprise) or free plans
  if (!plan || plan.isContactPlan || plan.price === 0) {
    return
  }

  // If not logged in, redirect to signup/beta page
  if (!isLoggedIn.value) {
    router.push('/beta')
    return
  }

  selectedPlanId.value = plan.id
  isCreatingCheckout.value = true

  try {
    // Get Stripe price ID for the selected plan
    const priceId = plan.priceId.stripe
    await subscribeToPlan(priceId, trpc)
  }
  catch (error) {
    toast({
      title: 'Erreur',
      description:
        error.message || 'Impossible de créer la session de paiement',
      variant: 'destructive',
    })
  }
  finally {
    isCreatingCheckout.value = false
    selectedPlanId.value = null
  }
}
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header section -->
    <header
      id="accueil"
      class="bg-white shadow"
      :style="
        hero.bg
          ? {
            backgroundImage: `url('${encodeURI(hero.bg)}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
          : {}
      "
    >
      <div class="max-w-7xl mx-auto px-4 py-6">
        <!-- Navigation -->
        <nav class="flex items-center justify-between">
          <div class="flex items-center">
            <logotype :title="navigation.logo" />
            <ul class="hidden md:flex ml-10 space-x-8">
              <li v-for="link in navigation.links" :key="link.name">
                <a
                  :href="link.href"
                  :class="
                    hero.whiteText
                      ? 'text-white hover:text-gray-200'
                      : 'text-black hover:text-gray-800'
                  "
                >{{ link.name }}</a>
              </li>
            </ul>
          </div>
          <div class="hidden md:flex items-center space-x-4">
            <Button
              variant="outline"
              class="flex items-center space-x-2 border-purple-500 text-purple-600"
              @click="$router.push(alternateRoute)"
            >
              <Globe class="w-4 h-4" />
              <span class="uppercase">{{ alternateLang }}</span>
            </Button>
            <Button :to="ctaLink">
              {{ hero.cta }}
            </Button>
          </div>

          <!-- Mobile menu Button -->
          <Button class="md:hidden" aria-label="Menu">
            <ChevronRight class="w-6 h-6" />
          </Button>
        </nav>

        <!-- Hero content -->
        <div class="mt-16 max-w-3xl mx-auto text-center">
          <p
            :class="
              hero.whiteText
                ? 'text-sm text-white mb-2'
                : 'text-sm text-black mb-2'
            "
          >
            {{ hero.tagline }}
          </p>
          <h1
            :class="
              hero.whiteText
                ? 'text-4xl font-bold text-white mb-4'
                : 'text-4xl font-bold text-black mb-4'
            "
          >
            {{ hero.title }}
          </h1>
          <p
            :class="
              hero.whiteText
                ? 'text-lg text-white mb-8'
                : 'text-lg text-black mb-8'
            "
          >
            {{ hero.description }}
          </p>
          <Button size="lg" :to="ctaLink" class=" ">
            {{ hero.cta }}
          </Button>
        </div>
      </div>
    </header>

    <!-- Stats section -->
    <section class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4">
        <div class="grid md:grid-cols-3 gap-8 text-center">
          <div v-for="(stat, index) in stats" :key="index" class="p-6">
            <div class="flex items-center justify-center mb-4">
              <component
                :is="stat.icon"
                class="w-8 h-8 text-purple-600 mx-auto"
              />
            </div>
            <div class="flex items-center justify-center">
              <span class="text-4xl font-bold text-purple-600">{{
                stat.value
              }}</span>
              <span class="text-xl text-gray-600">{{ stat.unit }}</span>
            </div>
            <p class="mt-2 text-gray-700">
              {{ stat.label }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Trusted by section -->
    <section class="py-16 bg-gray-50">
      <div
        class="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-center gap-8"
      >
        <img :src="trustedBy.imageSrc" alt="" class="w-full max-w-md">
        <div class="text-center md:text-left">
          <p class="text-xl font-semibold text-gray-700">
            {{ trustedBy.title }}
          </p>
          <p
            class="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600"
          >
            {{ trustedBy.count }}
          </p>
          <p class="text-xl font-semibold text-gray-700">
            {{ trustedBy.subtitle }}
          </p>
        </div>
      </div>
    </section>

    <!-- Features title -->
    <section id="fonctionnalites" class="py-16 bg-gray-50">
      <div class="max-w-3xl mx-auto px-4 text-center">
        <h2 class="text-3xl font-bold mb-4">
          <span
            class="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600"
          >
            {{ featuresTitle.title }}
          </span>
          <br>{{ featuresTitle.subtitle }}
        </h2>
        <p class="text-lg text-gray-600">
          {{ featuresTitle.description }}
        </p>
      </div>
    </section>

    <!-- Features -->
    <div v-for="(feature, fIndex) in features" :key="fIndex">
      <section
        class="py-16"
        :class="fIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
      >
        <div class="max-w-7xl mx-auto px-4">
          <div
            class="flex flex-col md:flex-row items-center gap-12" :class="[
              fIndex % 2 === 1 ? 'md:flex-row-reverse' : '',
            ]"
          >
            <!-- Feature content -->
            <div class="md:w-1/2">
              <div class="mb-4 p-2 inline-block rounded-full bg-purple-100">
                <component :is="feature.icon" class="w-6 h-6 text-purple-600" />
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">
                {{ feature.title }}
              </h3>
              <p class="text-gray-600 mb-8">
                {{ feature.description }}
              </p>
              <Button :to="ctaLink">
                {{ feature.cta }}
              </Button>
            </div>

            <!-- Feature image and widgets -->
            <div class="md:w-1/2 relative">
              <img
                :src="feature.imageSrc"
                alt=""
                class="w-full rounded-lg shadow-lg-DISABLD"
              >

              <!-- Feature specific widgets -->
              <template
                v-if="fIndex === 0 && feature.widgets && feature.widgets.length"
              >
                <!-- Simplified widgets for the first feature -->
                <div
                  v-show="false"
                  class="absolute top-4 right-4 bg-white rounded-lg shadow-md p-4 w-40"
                >
                  <p class="font-medium text-sm mb-2">
                    {{ feature.widgets[0].topText }}
                  </p>
                  <div class="flex justify-between items-center">
                    <div class="text-xl font-bold">
                      {{ feature.widgets[0].completed
                      }}<span class="text-purple-600">/{{ feature.widgets[0].total }}</span>
                    </div>
                    <div class="text-xs text-gray-600">
                      {{ feature.widgets[0].status }}
                    </div>
                  </div>
                </div>

                <!-- Progress widget simplified -->
                <div
                  v-show="false"
                  class="absolute bottom-4 left-4 bg-white rounded-lg shadow-md p-4 w-64"
                >
                  <p class="font-medium text-sm mb-4">
                    {{ feature.widgets[1].title }}
                  </p>
                  <div class="space-y-3">
                    <div
                      v-for="(metric, mIndex) in feature.widgets[1].metrics"
                      :key="`chart-${mIndex}`"
                      class="flex items-center justify-between"
                    >
                      <span class="text-sm">{{ metric.category }}</span>
                      <div class="w-24 bg-gray-200 rounded-full h-2.5">
                        <div
                          class="bg-purple-600 h-2.5 rounded-full"
                          :style="`width: ${metric.value}%`"
                        />
                      </div>
                      <span class="text-sm font-medium">{{ metric.value }}%</span>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Feature 2 specific messages -->
              <template v-if="fIndex === 1 && feature.messages">
                <div
                  class="absolute top-4 left-4 bg-white rounded-lg shadow-md p-4 w-64"
                >
                  <div
                    v-for="(message, mIndex) in feature.messages[0].topMessages"
                    :key="mIndex"
                    class="text-sm mb-2 last:mb-0"
                  >
                    {{ message }}
                  </div>
                </div>
                <div
                  class="absolute bottom-4 right-4 bg-white rounded-lg shadow-md p-4 w-64"
                >
                  <div
                    v-for="(message, mIndex) in feature.messages[0]
                      .bottomMessages"
                    :key="mIndex"
                    class="text-sm mb-2 last:mb-0"
                  >
                    {{ message }}
                  </div>
                </div>
              </template>

              <!-- Feature 3 specific resources -->
              <template v-if="fIndex === 2 && feature.resourceList">
                <div
                  class="absolute top-4 right-4 bg-white rounded-lg shadow-md p-4"
                >
                  <ul class="grid grid-cols-2 gap-3">
                    <li
                      v-for="(resource, rIndex) in feature.resourceList"
                      :key="rIndex"
                      class="flex items-center"
                    >
                      <component
                        :is="resource.icon"
                        class="w-4 h-4 mr-2 text-purple-600"
                      />
                      <span class="text-sm">{{ resource.text }}</span>
                    </li>
                  </ul>
                </div>

                <div
                  v-if="feature.readingPlan"
                  class="absolute bottom-4 left-4 bg-white rounded-lg shadow-md p-4 w-64"
                >
                  <p class="font-medium text-sm mb-3">
                    {{ feature.readingPlan.title }}
                  </p>
                  <div class="space-y-3">
                    <div
                      v-for="(item, iIndex) in feature.readingPlan.items"
                      :key="`exp-${iIndex}`"
                      class="flex"
                    >
                      <Book class="w-5 h-5 mr-2 text-purple-600" />
                      <div>
                        <p class="text-sm font-medium">
                          {{ item.title }}
                        </p>
                        <p class="text-xs text-gray-600">
                          {{ item.author }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Security section -->
    <section id="securite" class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold mb-4">
            {{ securitySection.title }}
          </h2>
          <p class="text-lg text-gray-600 max-w-3xl mx-auto">
            {{ securitySection.description }}
          </p>
        </div>

        <div class="grid md:grid-cols-4 gap-8">
          <div
            v-for="(feature, index) in securitySection.features"
            :key="index"
            class="bg-gray-50 p-6 rounded-lg"
          >
            <div class="mb-4 p-2 inline-block rounded-full bg-purple-100">
              <component :is="feature.icon" class="w-6 h-6 text-purple-600" />
            </div>
            <h3 class="text-xl font-semibold mb-2">
              {{ feature.title }}
            </h3>
            <p class="text-gray-600">
              {{ feature.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials slider -->
    <section id="temoignages" class="py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold mb-2">
            {{ testimonialHeader.title }}
          </h2>
          <h2
            class="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600"
          >
            {{ testimonialHeader.subtitle }}
          </h2>
        </div>

        <!-- Simplified slider -->
        <div class="relative">
          <div
            class="flex overflow-x-auto scrollbar-hide -mx-4 px-4 space-x-6 pb-8"
          >
            <div
              v-for="(slide, sIndex) in testimonialSlides"
              :key="sIndex"
              class="flex-shrink-0 w-full max-w-md bg-white rounded-xl shadow-md p-6"
            >
              <div
                class="w-12 h-12 rounded-full bg-gray-300 mb-4 flex items-center justify-center overflow-hidden"
              >
                <img
                  v-if="slide.avatarUrl"
                  :src="slide.avatarUrl"
                  alt="Avatar"
                  class="w-full h-full object-cover"
                >
                <User v-else class="w-6 h-6 text-gray-600" />
              </div>
              <blockquote class="text-xl italic text-gray-700 mb-4">
                {{ slide.quote }}
              </blockquote>
              <div>
                <p class="font-medium text-gray-900">
                  {{ slide.name }}
                </p>
                <p class="text-sm text-gray-600">
                  <span class="text-gray-500">{{ slide.position }}</span>
                  {{ slide.company }}
                </p>
              </div>
            </div>
          </div>

          <!-- Navigation arrows -->
          <Button class="absolute top-1/2 left-0 -translate-y-1/2">
            <ChevronLeft class="w-6 h-6" />
          </Button>
          <Button class="absolute top-1/2 right-0 -translate-y-1/2">
            <ChevronRight class="w-6 h-6" />
          </Button>
        </div>
      </div>
    </section>

    <!-- Pricing section -->
    <section id="pricing" class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold mb-4">
            <span
              class="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600"
            >
              {{ pricingContent.title }}
            </span>
          </h2>
          <p class="text-lg text-gray-600 max-w-3xl mx-auto">
            {{ pricingContent.description }}
          </p>
        </div>

        <div class="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
          <div
            v-for="plan in pricingPlans"
            :key="plan.id"
            class="relative rounded-lg border p-6"
            :class="{
              'border-primary shadow-lg': plan.popular,
              'border-muted': !plan.popular,
            }"
          >
            <!-- Popular badge -->
            <div
              v-if="plan.popular"
              class="absolute -top-3 left-1/2 -translate-x-1/2"
            >
              <Badge class="px-3 py-1 bg-purple-600 text-white">
                {{ pricingContent.popular }}
              </Badge>
            </div>

            <div class="space-y-4">
              <div>
                <h3 class="font-semibold text-lg">
                  {{ plan.name }}
                </h3>
                <div class="mt-2">
                  <span class="text-2xl font-bold">{{
                    formatPrice(plan.price)
                  }}</span>
                  <span class="text-muted-foreground ml-2">
                    {{
                      plan.billing === 'monthly'
                        ? pricingContent.monthly
                        : plan.billing === 'annual'
                          ? pricingContent.annual
                          : plan.billing === 'lifetime'
                            ? pricingContent.lifetime
                            : pricingContent.monthly
                    }}
                  </span>
                </div>
                <p class="mt-2 text-sm text-muted-foreground">
                  {{ plan.description }}
                </p>
              </div>

              <ul class="space-y-2 text-sm">
                <li
                  v-for="feature in plan.features"
                  :key="feature"
                  class="flex items-start gap-2"
                >
                  <CheckCircle
                    class="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5"
                  />
                  <span>{{ feature }}</span>
                </li>
              </ul>

              <!-- Contact Sales button for Enterprise plan -->
              <Button
                v-if="plan.isContactPlan"
                :variant="plan.popular ? 'default' : 'outline'"
                class="w-full"
                as="a"
                :href="`mailto:${plan.contactEmail}?subject=Demande%20Forfait%20Entreprise&body=Je%20suis%20intéressé%20par%20le%20forfait%20Entreprise%20de%20Selfwise.`"
                target="_blank"
              >
                {{ plan.cta }}
              </Button>

              <!-- Subscription button for regular plans -->
              <Button
                v-else
                :variant="plan.popular ? 'default' : 'outline'"
                class="w-full"
                :disabled="isCreatingCheckout && selectedPlanId === plan.id"
                @click="handleSubscribeToPlan(plan)"
              >
                <span
                  v-if="isCreatingCheckout && selectedPlanId === plan.id"
                >
                  <Loader2 class="h-4 w-4 mr-2 animate-spin" />
                  {{ pricingContent.processing }}
                </span>
                <span v-else>
                  {{ isLoggedIn ? plan.cta : pricingContent.getStarted }}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bottom section -->
    <section
      class="py-20 bg-gradient-to-br from-purple-600 to-purple-700 text-white"
    >
      <div
        class="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center"
      >
        <div class="md:w-2/3 text-center md:text-left">
          <p class="text-purple-200 mb-2">
            {{ bottomSection.tagline }}
          </p>
          <h2 class="text-4xl font-bold mb-4">
            {{ bottomSection.title }}
          </h2>
          <p class="text-purple-100 mb-8 max-w-2xl">
            {{ bottomSection.description }}
          </p>
          <Button
            :to="ctaLink"
            class="inline-block bg-white text-purple-600 font-medium px-6 py-3 rounded-lg hover:bg-purple-50"
          >
            {{ bottomSection.cta }}
          </Button>
        </div>
        <div class="md:w-1/3 mt-8 md:mt-0 flex justify-center">
          <logotype />
        </div>
      </div>
    </section>

    <!-- Language toggle button (mobile only) -->
    <div class="fixed bottom-4 right-4 md:hidden">
      <Button
        class="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2"
        @click="$router.push(alternateRoute)"
      >
        <Globe class="w-5 h-5" />
        <span class="uppercase">{{ alternateLang }}</span>
      </Button>
    </div>

    <BrochureFooter />
  </div>
</template>
